import { useTranslation } from "react-i18next";
import { contentFor, type Locale } from "@nh/shared";
import type { ResolvedPackageItem } from "../packages.catalog";

export interface PackageContentsProps {
  items: readonly ResolvedPackageItem[];
  /** Idioma del CONTENIDO del catálogo (viene del servidor, no de i18n). */
  locale: Locale;
  /** Cuántos productos se listan antes de resumir el resto. */
  max?: number;
}

/**
 * Qué incluye el paquete.
 *
 * ── El nombre no viaja dentro del paquete ────────────────────────────
 * `packageItemSchema` guarda `productId` y cantidad, y nada más. Así el
 * día que un producto se renombre, los paquetes que lo contienen dicen el
 * nombre nuevo en vez de arrastrar una copia vieja. El precio de la
 * tarjeta sigue siendo el del paquete: este emparejamiento es solo texto.
 *
 * ── Un producto sin pareja no desaparece ─────────────────────────────
 * El catálogo puede no haber llegado todavía, o el producto haberse
 * despublicado. En los dos casos la línea se pinta igual, con la cantidad
 * y un nombre genérico: un paquete de cinco piezas anunciado como de
 * cuatro cambia lo que la persona cree estar comprando.
 *
 * ── Y no se listan los diez ──────────────────────────────────────────
 * Cuatro y un resumen. La tarjeta tiene que poder compararse con la de al
 * lado de un vistazo, y una lista de diez líneas empuja el precio fuera
 * de la pantalla — que es justo el dato por el que la persona vino.
 *
 * El texto del producto se resuelve con `contentFor`, igual que en la
 * tienda: el nombre lo escribe quien administra el catálogo, no quien
 * programa, y por eso no vive en los archivos de traducción.
 */
export function PackageContents({ items, locale, max = 4 }: PackageContentsProps) {
  const { t } = useTranslation();
  const shown = items.slice(0, max);
  const rest = items.length - shown.length;

  return (
    <div>
      <p className="text-[13px] font-semibold text-ds-ink">{t("Incluye")}</p>

      <ul className="mt-2 space-y-1.5">
        {shown.map((item) => (
          <li
            key={item.productId}
            className="flex items-baseline gap-2 text-[14px] leading-snug text-ds-ink-3"
          >
            <span className="shrink-0 font-semibold tabular-nums text-ds-purple">
              {item.quantity}×
            </span>
            <span className="min-w-0 break-words">
              {item.product
                ? contentFor(item.product.translations, locale).name
                : t("Producto del paquete")}
            </span>
          </li>
        ))}
      </ul>

      {rest > 0 && (
        <p className="mt-2 text-[13px] text-ds-ink-3">
          {rest === 1
            ? t("y {{n}} producto más", { n: rest })
            : t("y {{n}} productos más", { n: rest })}
        </p>
      )}
    </div>
  );
}
